import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, CheckCircle2, Undo2, Crown, ShieldCheck } from "lucide-react";
import { draftApi } from "./editor/useDraftDocument";

const HIGH_RISK = new Set(["Senior Approved", "Eagle Approved", "Approved"]);
const STEPS = {
  Draft: [{ to: "Under Review", label: "Submit for review", icon: Send }],
  "Under Review": [{ to: "Senior Approved", label: "Senior approval", icon: ShieldCheck }, { to: "Draft", label: "Return to drafter", icon: Undo2 }],
  "Senior Approved": [{ to: "Eagle Approved", label: "Eagle approval", icon: Crown, eagle: true }, { to: "Approved", label: "Final approve", icon: CheckCircle2, final: true }, { to: "Draft", label: "Return", icon: Undo2 }],
  "Eagle Approved": [{ to: "Approved", label: "Final approve", icon: CheckCircle2 }, { to: "Draft", label: "Return", icon: Undo2, eagle: true }],
  Approved: [{ to: "Draft", label: "Reopen (new version)", icon: Undo2 }],
};

// Approval chain: Author → Reviewer → Senior → (Eagle, if org policy) → Approved. Server enforces roles.
export function DraftReviewFlow({ draft, policy, onBeforeTransition, onTransitioned }) {
  const { personaRole } = useApp();
  const [pending, setPending] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const steps = (STEPS[draft.status] || STEPS.Draft).filter((s) => !(s.final && policy?.eagleApprovalRequired)).filter((s) => !s.eagle || personaRole === "Managing Partner");

  const commit = async () => {
    setBusy(true);
    try {
      await onBeforeTransition?.();
      const r = await draftApi.transition(draft.id, { to: pending.to, note: note.trim(), confirm: true });
      toast.success(`Draft marked ${r.status}.${r.locked ? " Approved version is now immutable." : ""}`);
      onTransitioned?.(r); setPending(null); setNote("");
    } catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : d?.message || "Transition failed"); } finally { setBusy(false); }
  };

  return (
    <>
      {steps.map((s) => <Button key={s.to} size="sm" variant={s.to === "Approved" ? "default" : "outline"} onClick={() => setPending(s)} data-testid={`review-${s.to.replace(/\s/g, "-").toLowerCase()}`}><s.icon className="mr-1 h-3.5 w-3.5" /> {s.label}</Button>)}
      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent data-testid="review-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{pending?.label}</DialogTitle><DialogDescription>{HIGH_RISK.has(pending?.to) ? "High-risk action: the current text is saved, hashed and logged. Final approval freezes this version — later edits create a new version." : "The current text is saved and the transition is logged on the draft."}</DialogDescription></DialogHeader>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Review note (what was checked, what remains)…" data-testid="review-note" />
          {(draft.reviewLog || []).length > 0 && <div className="max-h-32 space-y-1 overflow-y-auto text-xs text-ink-2">{[...draft.reviewLog].reverse().map((l, i) => <div key={i}><span className="text-ink-muted">{String(l.at).slice(0, 16).replace("T", " ")}</span> · {l.from} → <b>{l.to}</b> by {l.byName || l.by}{l.note ? ` — ${l.note}` : ""}</div>)}</div>}
          <DialogFooter><Button variant="ghost" onClick={() => setPending(null)}>Cancel</Button><Button disabled={busy} onClick={commit} data-testid="review-confirm">{HIGH_RISK.has(pending?.to) ? "I have reviewed — confirm" : "Confirm"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
